import { Maybe } from 'graphql/__generated__/graphqlApi';
import * as React from 'react';
import { FC, useCallback, useMemo } from 'react';
import { SxProps, styled } from '@mui/material/styles';
import { Stack, Typography, useTheme } from '@mui/material';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import { useLang } from '../../utils/context/LangContext';
import { MultiLineTypography } from '../../components/atoms/common/MultiLineTypography';
import { getTextColor } from '../../utils/ui/getTextColor';
import { ReactComponent as PlayIcon } from 'assets/svgs/replay.svg';

const Container = styled(Stack)`
  flex: 1;
`;
const TitleWrapper = styled(Stack)(
  ({ theme }) => `
  padding-bottom: 8px;
  flex-direction: row;
  align-items: center;
  border-bottom: 2px solid ${theme.colors.gray.base};
`,
);
const RaceResult = styled(MultiLineTypography)(
  ({ theme }) => `
  flex: 1;
  font-size: 16px;
  color: ${theme.colors.tertiary.base};
`,
);
const PlayWrapper = styled(Stack)(
  ({ theme }) => `
  gap: 4px;
  padding: 8px;
  flex-direction: row;
  border-radius: 10px;
  align-items: center;
  border: 1px solid ${theme.colors.black.base};
`,
);
const ReplayLabel = styled(Typography)(
  ({ theme }) => `
  font-size: 12px;
  line-height: 13.2px;
  color: ${theme.colors.tertiary.base};
`,
);
const ValueLabel = styled(MultiLineTypography)<{ color?: string }>(
  ({ theme, color }) => `
  font-size: 12px;
  font-weight: bold;
  line-height: 16px;
  color: ${color ?? theme.colors.tertiary.base};
`,
);
const BlurView = styled(Stack)(
  ({ theme }) => `
  background-color: ${theme.colors.tertiary.darkness4};
  position: relative;
  filter: blur(4px);
  transition: filter 0.3s ease-in-out;
`,
);

interface Results {
  result1: { isLocked?: boolean; value: string; id: string }[];
  result2: { isLocked?: boolean; value: string; id: string }[];
  id: string;
  colorCode?: string;
}

export enum StatsType {
  STATS = 'STATS',
  RESULTS = 'RESULTS',
}

export interface DataStats {
  results: Results[];
  headers1: string[];
  headers2: string[];
  title?: Maybe<string>;
  type?: StatsType;
  replayVideoUrl?: Maybe<string>;
}

interface RaceResultContainerProps {
  sx?: SxProps;
  isStats?: boolean;
  isPopup?: boolean;
  hideTitle?: boolean;
  dataStats: DataStats | null;
  onReplayMatchResultVideo?(matchVideoResult: string | null): void;
}

const RaceResultContainer: FC<RaceResultContainerProps> = ({
  sx,
  isStats,
  isPopup,
  hideTitle,
  dataStats,
  onReplayMatchResultVideo,
}) => {
  const { t } = useLang();
  const theme = useTheme();

  const indexOfLane = useMemo(() => {
    if (dataStats?.headers1.some(item => item === 'No.')) {
      return dataStats?.headers1.indexOf('No.');
    }
    if (dataStats?.headers1.some(item => item === 'number')) {
      return dataStats?.headers1.indexOf('number');
    }
    if (dataStats?.headers1.some(item => item === 'Side')) {
      return dataStats?.headers1.indexOf('Side');
    }
    return dataStats?.headers1.indexOf(t('lane'));
  }, [dataStats?.headers1, t]);
  const indexOfPlayer = useMemo(() => {
    return dataStats?.headers1.indexOf(t('player'));
  }, [dataStats?.headers1, t]);
  const indexOfPoint = useMemo(() => {
    return dataStats?.headers2.indexOf(t('point'));
  }, [dataStats?.headers2, t]);
  const indexOfEarnedPoint = useMemo(() => {
    return dataStats?.headers2.indexOf(t('earnedPoint'));
  }, [dataStats?.headers2, t]);

  const renderBlurView = useCallback(
    (isLocked: boolean | undefined, value: string) => {
      if (!isLocked) {
        return <ValueLabel>{value}</ValueLabel>;
      }
      return (
        <BlurView>
          <ValueLabel>{value}</ValueLabel>
        </BlurView>
      );
    },
    [],
  );

  const getLeft = useCallback(
    (isPlayer: boolean, index: number) => {
      if (dataStats?.headers1.length === 3) {
        if (isPlayer) {
          return 80;
        }
        if (index === 1) {
          return 40;
        }
        return 0;
      }
      if (dataStats?.headers1.length === 2) {
        if (index === 1) {
          return 40;
        }
        return 0;
      }
    },
    [dataStats?.headers1.length],
  );

  const getValueResult = useCallback((value: string, isPlayer: boolean) => {
    if (isPlayer) {
      if (value.length > 15) {
        return `${value.slice(0, 12)}...`;
      }
      return value;
    }
    if (value.length > 7) {
      return `${value.slice(0, 4)}...`;
    }
    return value;
  }, []);

  const generateColor = useCallback((isLane: boolean, colorCode?: string) => {
    if (isLane && colorCode) {
      return colorCode;
    }
    return '#FFFFFF';
  }, []);

  const statsTitle = useMemo(() => {
    if (!isStats) return dataStats?.title;
    if (dataStats?.type === StatsType.STATS) {
      return `${dataStats?.title} - Race Stat`;
    }
    if (dataStats?.type === StatsType.RESULTS) {
      return `${dataStats?.title} - Race Result`;
    }
    return dataStats?.title;
  }, [dataStats?.title, dataStats?.type, isStats]);

  const onReplay = useCallback(() => {
    if (onReplayMatchResultVideo) {
      onReplayMatchResultVideo(dataStats?.replayVideoUrl ?? null);
    }
  }, [dataStats?.replayVideoUrl, onReplayMatchResultVideo]);

  return (
    <Container sx={sx}>
      {!hideTitle ? (
        <TitleWrapper>
          <RaceResult fontWeight={'700'} lineHeight={'20px'}>
            {statsTitle ?? ''}
          </RaceResult>
          {dataStats?.type === StatsType.RESULTS &&
          dataStats?.replayVideoUrl ? (
            <PlayWrapper onClick={onReplay}>
              <PlayIcon width={10} height={10} />
              <ReplayLabel fontWeight={'400'}>{t('replay')}</ReplayLabel>
            </PlayWrapper>
          ) : (
            <></>
          )}
        </TitleWrapper>
      ) : null}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow
              sx={{ borderBottom: `1px solid ${theme.colors.gray.base}` }}
            >
              {dataStats?.headers1?.map((item, index) => {
                const isPlayer = item === t('player');
                const left = getLeft(isPlayer, index);
                return (
                  <TableCell
                    key={item}
                    sx={{
                      left,
                      border: 0,
                      zIndex: 1,
                      position: 'sticky',
                      background: '#FFFFFF',
                      padding: isPlayer ? '12px 4px' : '12px 0px',
                      minWidth: isPlayer
                        ? isPopup
                          ? '100px'
                          : '130px'
                        : '40px',
                    }}
                    align={isPlayer ? 'left' : 'center'}
                  >
                    <ValueLabel>{item}</ValueLabel>
                  </TableCell>
                );
              })}
              {dataStats?.headers2?.map((item, indexRow) => {
                const isPoint = indexRow === indexOfPoint;
                const isEarnedPoint = indexRow === indexOfEarnedPoint;
                return (
                  <TableCell
                    key={item}
                    sx={{
                      border: 0,
                      padding: '12px 4px',
                      minWidth: isEarnedPoint
                        ? '70px'
                        : isPopup || isPoint
                        ? '50px'
                        : '70px',
                    }}
                    align='center'
                  >
                    <ValueLabel>{item}</ValueLabel>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {dataStats?.results?.map(stat => (
              <TableRow
                key={stat.id}
                sx={{ borderBottom: `1px solid ${theme.colors.gray.base}` }}
              >
                {stat?.result1?.map((option, indexRow) => {
                  const isLane = indexRow === indexOfLane;
                  const isPlayer = indexRow === indexOfPlayer;
                  const left = getLeft(isPlayer, indexRow);
                  return (
                    <TableCell
                      key={`${option.id}_${option.value}`}
                      align={isPlayer ? 'left' : 'center'}
                      sx={{
                        left,
                        border: 0,
                        zIndex: 1,
                        position: 'sticky',
                        background: '#FFFFFF',
                        padding: isLane ? '2px 12px' : '2px 0',
                        minWidth: isPlayer
                          ? isPopup
                            ? '100px'
                            : '130px'
                          : '40px',
                      }}
                    >
                      <Stack
                        sx={{
                          padding: isPlayer ? '12px 4px' : '12px 0px',
                          backgroundColor: generateColor(
                            isLane,
                            stat.colorCode,
                          ),
                        }}
                      >
                        {isLane ? (
                          <ValueLabel color={getTextColor(stat.colorCode)}>
                            {getValueResult(option.value, isPlayer)}
                          </ValueLabel>
                        ) : (
                          <ValueLabel>
                            {getValueResult(option.value, isPlayer)}
                          </ValueLabel>
                        )}
                      </Stack>
                    </TableCell>
                  );
                })}
                {stat?.result2?.map((option, indexRow) => {
                  const isPoint = indexRow === indexOfPoint;
                  const isEarnedPoint = indexRow === indexOfEarnedPoint;
                  return (
                    <TableCell
                      key={`${option.id}_${option.value}`}
                      sx={{
                        border: 0,
                        padding: '16px 4px',
                        minWidth: isEarnedPoint
                          ? '70px'
                          : isPopup || isPoint
                          ? '50px'
                          : '70px',
                      }}
                      align='center'
                    >
                      {renderBlurView(option.isLocked, option.value)}{' '}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default RaceResultContainer;
